import type {
  ComicCard,
  ComicDetails,
  ComicSource,
  Page,
  PageRef,
  PopularOpts,
  RecommendationOpts,
  SearchFiltersSchema,
  SearchQuery,
  SourceAuth,
  SourceCommentOpts,
  SourceCommentPage,
  SourceId,
} from '../types.ts';
import type { ProxyFetchOpts } from '../../lib/proxy.ts';
import { loadVeneraPlugin } from '../_venera/load.ts';
import { mapVeneraPageResult } from '../_venera/mapper.ts';
import type { VeneraComicSource } from '../_venera/types.ts';
import { createPicacgRelogin, readPicacgToken, type PicacgSavedLogin } from './relogin.ts';

export const PICACG_SOURCE_ID = 'picacg' satisfies SourceId;

export const PICACG_DEFAULTS = {
  base_url: 'https://picaapi.picacomic.com',
  imageQuality: 'original',
  appChannel: '3',
  favoriteSort: 'dd',
} as const;

const PICACG_RECOMMENDATION_OPTIONS = [
  { id: 'h24', label: '24 hours', exploreKey: 'Picacg H24', default: true },
  { id: 'd7', label: '7 days', exploreKey: 'Picacg D7' },
  { id: 'd30', label: '30 days', exploreKey: 'Picacg D30' },
] as const;

const PICACG_CATEGORIES = [
  '大家都在看',
  '大濕推薦',
  '那年今天',
  '官方都在看',
  '嗶咔漢化',
  '全彩',
  '長篇',
  '同人',
  '短篇',
  '圓神領域',
  '碧藍幻想',
  'CG雜圖',
  '英語 ENG',
  '生肉',
  '純愛',
  '百合花園',
  '耽美花園',
  '偽娘哲學',
  '後宮閃光',
  '扶他樂園',
  '單行本',
  '姐姐系',
  '妹妹系',
  'SM',
  '性轉換',
  '足の恋',
  '人妻',
  'NTR',
  '強暴',
  '非人類',
  '艦隊收藏',
  'Love Live',
  'SAO 刀劍神域',
  'Fate',
  '東方',
  'WEBTOON',
  '禁書目錄',
  '歐美',
  'Cosplay',
  '重口地帶',
] as const;

const PICACG_SEARCH_FILTERS: SearchFiltersSchema = {
  fields: [
    {
      id: 'sort',
      type: 'select',
      label: 'Sort',
      defaultValue: 'dd',
      options: [
        { value: 'dd', label: 'New to old' },
        { value: 'da', label: 'Old to new' },
        { value: 'ld', label: 'Most likes' },
        { value: 'vd', label: 'Most nominated' },
      ],
    },
    {
      id: 'picacgCategory',
      type: 'select',
      label: 'Category',
      options: PICACG_CATEGORIES.map((category) => ({ value: category, label: category })),
    },
    {
      id: 'includeTags',
      type: 'tags',
      label: 'Tag / author',
      placeholder: 'Tags:肛內中出 or Author:name',
    },
  ],
};

export interface CreatePicacgSourceOptions {
  jsSource: string;
  proxyFetch?: (url: string, opts?: ProxyFetchOpts) => Promise<Response>;
  saveAuth?: (sourceId: SourceId, auth: SourceAuth, opts?: { username?: string }) => Promise<void>;
  /** Device-saved login used to renew expired tokens automatically. */
  loadSavedLogin?: () => Promise<PicacgSavedLogin | undefined>;
}

export function createPicacgSource(options: CreatePicacgSourceOptions): ComicSource {
  let delegatePromise: Promise<VeneraComicSource> | undefined;

  async function delegate(): Promise<VeneraComicSource> {
    delegatePromise ??= loadVeneraPlugin({
      sourceId: PICACG_SOURCE_ID,
      jsSource: options.jsSource,
      defaults: PICACG_DEFAULTS,
      settings: PICACG_DEFAULTS,
      defaultExploreKey: 'Picacg Latest',
      searchFilters: PICACG_SEARCH_FILTERS,
      capabilities: source.capabilities,
      recommendationExploreKeys: [...PICACG_RECOMMENDATION_OPTIONS],
      proxyFetch: options.proxyFetch,
      saveAuth: options.saveAuth,
    });
    return delegatePromise;
  }

  const relogin = createPicacgRelogin({
    loadSavedLogin: options.loadSavedLogin ?? (async () => undefined),
    async login(saved) {
      const result = await (await delegate()).loginWithForm?.(saved);
      const token = readPicacgToken(result?.auth);
      if (!result?.ok || !token) throw new Error(result?.reason ?? 'Picacg returned no token.');
      return token;
    },
  });

  function withDefaultSort(query: SearchQuery): SearchQuery {
    const extra = { ...(query.extra ?? {}) };
    if (extra.sort === undefined && query.sort === undefined) extra.sort = PICACG_DEFAULTS.favoriteSort;
    return { ...query, extra };
  }

  const source: ComicSource = {
    id: PICACG_SOURCE_ID,
    name: 'Picacg',
    description: 'Venera-backed Picacg/Bika source with signed API requests.',
    capabilities: {
      login: true,
      favorites: true,
      multiFavCategories: false,
      search: true,
      advancedSearch: true,
      popularRanges: ['Picacg Latest', 'Picacg H24', 'Picacg D7', 'Picacg D30'],
      recommendations: true,
      comments: true,
      rememberLogin: true,
      loginNotes:
        'Picacg uses username/password login. Bunko sends credentials to Picacg through the proxy worker and stores the returned API token in encrypted source auth. Tick "Remember login on this device" to let Bunko renew expired tokens automatically; the password stays encrypted on this device and is never synced.',
    },
    searchFilters: PICACG_SEARCH_FILTERS,
    recommendationOptions: PICACG_RECOMMENDATION_OPTIONS.map(({ exploreKey: _exploreKey, ...option }) => option),
    async search(query: SearchQuery, auth?: SourceAuth): Promise<Page<ComicCard>> {
      const tagAction = picacgTagAction(query);
      if (tagAction?.type === 'browse') {
        const result = await (await delegate()).testInternals.callPluginMethod(
          'loadComicListByTag',
          [tagAction.kind, tagAction.value, tagAction.sort, Math.max(1, query.page ?? 1)],
          auth,
        );
        return mapVeneraPageResult(result, PICACG_SOURCE_ID, { page: Math.max(1, query.page ?? 1) });
      }
      if (tagAction?.type === 'keyword') {
        return (await delegate()).search(
          withDefaultSort({ ...query, q: tagAction.value, includeTags: undefined }),
          auth,
        );
      }
      return (await delegate()).search(withDefaultSort(query), auth);
    },
    async getPopular(opts?: PopularOpts, auth?: SourceAuth): Promise<Page<ComicCard>> {
      return (await delegate()).getPopular(opts, auth);
    },
    async getRecommendations(opts?: RecommendationOpts, auth?: SourceAuth): Promise<Page<ComicCard>> {
      return (await delegate()).getRecommendations?.(opts, auth) ?? { items: [], page: opts?.page ?? 1, hasMore: false };
    },
    async getComic(id: string, auth?: SourceAuth): Promise<ComicDetails> {
      const details = await (await delegate()).getComic(id, auth);
      return { ...details, sourceUrl: picacgSourceUrl(id) };
    },
    async getPages(id: string, auth?: SourceAuth): Promise<PageRef[]> {
      return (await delegate()).getPages(id, auth);
    },
    async getComments(id: string, auth?: SourceAuth, opts?: SourceCommentOpts): Promise<SourceCommentPage> {
      return (await delegate()).getComments!(id, auth, opts);
    },
    async resolveImageUrl(ref: PageRef, auth?: SourceAuth): Promise<{ url: string }> {
      return (await delegate()).resolveImageUrl(ref, auth);
    },
    async validateAuth(auth: SourceAuth): Promise<{ ok: boolean; username?: string; reason?: string }> {
      const token = readPicacgToken(auth);
      return token ? { ok: true } : { ok: false, reason: 'Missing Picacg API token.' };
    },
    async loginWithForm(request) {
      return (await delegate()).loginWithForm?.(request) ?? { ok: false, reason: 'Login unavailable.' };
    },
    reconcileAuth: (auth) => relogin.reconcile(auth),
    onAuthRefreshed: (listener) => relogin.onRefreshed(listener),
    async listFavorites(auth, opts) {
      return (await delegate()).listFavorites?.(auth, opts) ?? { items: [], page: opts.page ?? 1, hasMore: false };
    },
    async addFavorite(id, auth, category) {
      await (await delegate()).addFavorite?.(id, auth, category);
    },
    async removeFavorite(id, auth) {
      await (await delegate()).removeFavorite?.(id, auth);
    },
  };

  // Every method that sends the token re-logs in once with the device-saved
  // password when Picacg reports the login expired.
  source.search = relogin.wrap(source.search, 1);
  source.getPopular = relogin.wrap(source.getPopular, 1);
  source.getRecommendations = relogin.wrap(source.getRecommendations!, 1);
  source.getComic = relogin.wrap(source.getComic, 1);
  source.getPages = relogin.wrap(source.getPages, 1);
  source.getComments = relogin.wrap(source.getComments!, 1);
  source.listFavorites = relogin.wrap(source.listFavorites!, 0);
  source.addFavorite = relogin.wrap(source.addFavorite!, 1);
  source.removeFavorite = relogin.wrap(source.removeFavorite!, 1);

  return source;
}

type PicacgTagAction =
  | { type: 'browse'; kind: 'a' | 'c'; value: string; sort: string }
  | { type: 'keyword'; value: string };

function picacgTagAction(query: SearchQuery): PicacgTagAction | undefined {
  if (query.q?.trim()) return undefined;
  const category = typeof query.extra?.picacgCategory === 'string' ? query.extra.picacgCategory.trim() : '';
  if (category) {
    const sort = typeof query.extra?.sort === 'string' ? query.extra.sort : query.sort ?? 'dd';
    return { type: 'browse', kind: 'c', value: category, sort };
  }
  const include = query.includeTags?.find((tag) => tag.trim());
  if (!include) return undefined;
  const separator = include.indexOf(':');
  const namespace = separator > 0 ? include.slice(0, separator).trim().toLowerCase() : '';
  const value = (separator > 0 ? include.slice(separator + 1) : include).trim();
  if (!value) return undefined;
  const sort = typeof query.extra?.sort === 'string' ? query.extra.sort : query.sort ?? 'dd';
  if (namespace === 'author') return { type: 'browse', kind: 'a', value, sort };
  if (namespace === 'categories' || namespace === 'category') return { type: 'browse', kind: 'c', value, sort };
  return { type: 'keyword', value };
}

function picacgSourceUrl(id: string): string {
  return `https://manhuabika.com/comic/${encodeURIComponent(id)}`;
}
